import classNames from 'classnames'
import { P } from 'components'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { XCircle } from 'react-feather'
import { Badge } from 'reactstrap'

const PMBBadge = ({
  variant = 'gray',
  bordered = false,
  title,
  description,
  timeout,
  className,
  children,
  iconStart,
  iconEnd,
  closeIcon = false,
  size = 'regular',
  testId = 'pmb-badge',
}) => {
  const [isClosing, setIsClosing] = useState(false)
  const [visible, setVisible] = useState(true)

  // AUTO-CLOSE WHEN TIMEOUT IS SET
  useEffect(() => {
    if (timeout) {
      const autoCloseTimer = setTimeout(() => handleClose(), timeout)
      return () => clearTimeout(autoCloseTimer)
    }
  }, [timeout])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => setVisible(false), 300)
  }

  const badgeClass = classNames(
    `badge-${variant}`,
    bordered && `border-${variant}`,
    'd-flex flex-column justify-content-center text-center rounded-3 p-2 w-100 text-wrap fade-badge',
    isClosing ? 'fade-out' : 'fade-in',
    className,
  )

  if (!visible) return null

  return (
    <Badge
      className={badgeClass}
      color=""
      data-testid={testId}
      style={{
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflow: 'hidden',
      }}
    >
      <div className="d-flex align-items-center justify-content-between w-100">
        {iconStart && (
          <div className="me-2 d-flex align-items-center">{iconStart}</div>
        )}

        <div className="flex-grow-1 text-center">
          {title && (
            <P size={size} bold className="text-break m-0">
              {title}
            </P>
          )}
          {description && (
            <P size="small" className="text-muted text-break m-0">
              {description}
            </P>
          )}
          {children}
          {iconEnd && <div className="mt-2">{iconEnd}</div>}
        </div>

        {closeIcon && (
          <div
            onClick={handleClose}
            className="ms-2 d-flex align-items-center justify-content-center"
            role="button"
            tabIndex={0}
            data-testid={`${testId}-close`}
            style={{ cursor: 'pointer' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClose()
            }}
          >
            <XCircle size={18} color="red" />
          </div>
        )}
      </div>
    </Badge>
  )
}

PMBBadge.propTypes = {
  variant: PropTypes.oneOf([
    'verify',
    'warning',
    'process',
    'cancel',
    'danger',
    'blues',
    'gray',
  ]),
  bordered: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  timeout: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node,
  iconStart: PropTypes.node,
  iconEnd: PropTypes.node,
  closeIcon: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'regular']),
}

export default PMBBadge
